import React, { useState } from 'react';
import { Box, IconButton, Menu, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import { colors, typography } from '../../../styles/theme';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import { listCardActionIconSx } from '../UserLotListsPanel/UserLotListsPanel.styles';
import {
  canLocateCadParcelOnCountryMap,
  getCadParcelCardLabel,
  getCadRegistrosCardMeta,
  getCadRegistrosListName,
} from '../../../Showroom/utils/cadRegistros';
import RecordCommercialLine from '../../../Leads/components/RecordCommercialLine';
import { recordCardHeaderIconSx } from '../RecordLotIdentity';

const BG_ELEVATED = '#242424';
const TEXT_PRIMARY = '#D4D4D4';
const CARD_PAD_X = 12;
const HEADER_GAP = 8;

/**
 * Card de parcela CAD en REGISTROS. Misma geometría que `RegistrosLotCard`.
 * Click abre el detalle in-panel (Listas), sin cambiar de ruta.
 */
export default function RegistrosCadParcelCard({
  parcel,
  onLocate,
  onOpen,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const title = getCadParcelCardLabel(parcel);
  const listName = getCadRegistrosListName(parcel);
  const metaLabel = getCadRegistrosCardMeta(parcel);
  const listTooltip = listName ? `Lista: ${listName}` : 'Lista';
  const canLocate = canLocateCadParcelOnCountryMap(parcel);

  const openLot = () => {
    onOpen?.(parcel);
  };

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box
      onClick={openLot}
      sx={{
        borderRadius: '8px',
        border: 'none',
        backgroundColor: BG_ELEVATED,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: '#2a2a2a' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: `${HEADER_GAP}px`,
          px: `${CARD_PAD_X}px`,
          pt: '10px',
          pb: '10px',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, height: 18 }}>
            <Typography
              title={title}
              sx={{
                color: TEXT_PRIMARY,
                fontFamily: typography.fontFamily.heading,
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '18px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                flex: '0 1 auto',
              }}
            >
              {title}
            </Typography>
            <Tooltip title={listTooltip} placement="top" arrow enterTouchDelay={0} leaveTouchDelay={4000}>
              <Box
                component="span"
                aria-label={listTooltip}
                onClick={(event) => event.stopPropagation()}
                sx={{ display: 'inline-flex', flexShrink: 0, color: colors.primaryLight, lineHeight: 0 }}
              >
                <GridOnOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ minHeight: 24, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Typography
              title={metaLabel}
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                color: '#ABABAB',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {metaLabel}
            </Typography>
          </Box>
          <Box sx={{ minHeight: 16, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <RecordCommercialLine summary={parcel?.commercial_summary} isCad showZero />
            </Box>
          </Box>
        </Box>

        <Box onClick={(event) => event.stopPropagation()} sx={{ flexShrink: 0, height: 18, display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Más acciones" placement="top" arrow>
            <IconButton
              size="small"
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{ ...listCardActionIconSx, ...recordCardHeaderIconSx }}
              aria-label="Más acciones"
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={ACTION_MENU_PAPER_PROPS}
            MenuListProps={ACTION_MENU_LIST_PROPS}
          >
            <ActionMenuItem
              onClick={() => {
                closeMenu();
                if (canLocate) onLocate?.(parcel);
              }}
              disabled={!canLocate}
              icon={<MyLocationOutlinedIcon sx={{ fontSize: 20 }} />}
              label="Ubicar en el mapa"
            />
            <ActionMenuItem
              onClick={() => {
                closeMenu();
                openLot();
              }}
              icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
              label="Ver lote"
            />
          </Menu>
        </Box>
      </Box>
    </Box>
  );
}
